import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { Sort } from '@angular/material/sort';
import { PageEvent } from '@angular/material/paginator';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { HotToastService } from '@ngxpert/hot-toast';
import { GenericListComponent } from '../../../shared/generic-list/generic-list.component';
import { ColumnDefinition } from '../../../../models/columnDefinition';
import { AuditLog } from '../../../../models/auditLog';
import { AuditLogSearchFilter } from '../../../../models/auditLogSearchFilter';
import { AuditLogSearchRequest } from '../../../../models/api/auditLogSearchRequest';
import { AuditLogService } from '../../../../services/audit-log.service';
import { AuditLogSearchBoxComponent } from '../audit-log-search-box/audit-log-search-box.component';
import { AuditLogChangesDialogComponent } from '../audit-log-changes-dialog/audit-log-changes-dialog.component';
import {
  AuditLogChangeSetDialogComponent,
  AuditLogChangeSetDialogData
} from '../audit-log-change-set-dialog/audit-log-change-set-dialog.component';

@Component({
  selector: 'app-audit-log-list',
  imports: [
    CommonModule,
    GenericListComponent,
    MatIconModule,
    MatButtonModule,
    NgxSpinnerModule,
    AuditLogSearchBoxComponent
  ],
  templateUrl: './audit-log-list.component.html',
  styleUrl: './audit-log-list.component.scss'
})
export class AuditLogListComponent implements OnInit {
  auditLogs: AuditLog[] = [];
  totalItems = 0;
  isSearchVisible = false;

  currentPage = 1;
  pageSize = 25;
  sortBy = 'changedAt';
  ascending = false;

  // Defaults to the current day until the user searches.
  currentSearchFilter: AuditLogSearchFilter = {
    changedBy: '',
    changedAtFrom: AuditLogListComponent.startOfToday(),
    changedAtTo: undefined,
    companyId: undefined
  };

  columnDefinitions: ColumnDefinition[] = [
    { propertyName: 'changedAt', displayName: 'Date', dataType: 'datetime' },
    { propertyName: 'changedBy', displayName: 'User' },
    { propertyName: 'entityType', displayName: 'Entity' },
    { propertyName: 'action', displayName: 'Action' },
    { propertyName: 'owningEntityType', displayName: 'Owner' }
  ];

  // Distinct, readable colours rotated across change sets that span more than one row on the page.
  private static readonly CHANGE_SET_PALETTE = [
    '#1976d2', '#388e3c', '#d32f2f', '#f57c00', '#7b1fa2', '#0097a7', '#c2185b', '#5d4037'
  ];

  /** changeSetId → accent colour, populated only for multi-row sets in the current page. */
  private changeSetColors = new Map<string, string>();

  /** Colours rows so entries from the same save (change set) read as one vertical band. */
  rowAccentFn = (log: AuditLog): string | null => this.changeSetColors.get(log.changeSetId) ?? null;

  constructor(
    private route: ActivatedRoute,
    private spinnerService: NgxSpinnerService,
    private auditLogService: AuditLogService,
    private dialog: MatDialog,
    private toast: HotToastService
  ) {}

  /** Midnight at the start of today (local time). */
  private static startOfToday(): Date {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
  }

  ngOnInit(): void {
    this.applyQueryParamFilter();
    this.loadAuditLogs();
  }

  /**
   * When opened as a targeted deep link (e.g. the audit log "for this company" or "for this user"),
   * the `companyId` / `changedBy` query params seed the filter and the default "today only" start
   * date is dropped, so the full history for that subject is shown rather than just today's.
   */
  private applyQueryParamFilter(): void {
    const params = this.route.snapshot.queryParamMap;
    const companyId = params.get('companyId') ?? undefined;
    const changedBy = params.get('changedBy') ?? undefined;

    if (companyId || changedBy) {
      this.currentSearchFilter = {
        changedBy: changedBy ?? '',
        changedAtFrom: undefined,
        changedAtTo: undefined,
        companyId
      };
    }
  }

  private loadAuditLogs(): void {
    this.spinnerService.show();

    const request: AuditLogSearchRequest = {
      pageNumber: this.currentPage,
      pageSize: this.pageSize,
      sortBy: this.sortBy,
      ascending: this.ascending,
      changedBy: this.currentSearchFilter.changedBy || undefined,
      changedAtFrom: this.currentSearchFilter.changedAtFrom?.toISOString(),
      changedAtTo: this.currentSearchFilter.changedAtTo?.toISOString(),
      companyId: this.currentSearchFilter.companyId?.trim() || undefined
    };

    this.auditLogService.searchAuditLogs(request).subscribe({
      next: (response) => {
        this.auditLogs = response.data;
        this.totalItems = response.totalCount;
        this.computeChangeSetColors();
        this.spinnerService.hide();
      },
      error: (error) => {
        console.error('Error loading audit logs:', error);
        this.toast.error('Error loading audit logs');
        this.spinnerService.hide();
      }
    });
  }

  onViewChanges(auditLog: AuditLog): void {
    this.dialog.open(AuditLogChangesDialogComponent, {
      data: auditLog,
      width: '720px',
      maxWidth: '90vw',
      autoFocus: false
    });
  }

  /** Opens every entry written alongside this one in the same save. */
  onViewChangeSet(auditLog: AuditLog): void {
    const data: AuditLogChangeSetDialogData = {
      changeSetId: auditLog.changeSetId,
      changedBy: auditLog.changedBy,
      changedAt: auditLog.changedAt
    };

    this.dialog.open(AuditLogChangeSetDialogComponent, {
      data,
      width: '820px',
      maxWidth: '90vw',
      autoFocus: false
    });
  }

  /**
   * Assigns each change set that has more than one row on the current page a palette colour, in
   * order of first appearance. Single-row sets get none — they need no visual linking.
   */
  private computeChangeSetColors(): void {
    const counts = new Map<string, number>();
    for (const log of this.auditLogs) {
      counts.set(log.changeSetId, (counts.get(log.changeSetId) ?? 0) + 1);
    }

    const colors = new Map<string, string>();
    let next = 0;
    const palette = AuditLogListComponent.CHANGE_SET_PALETTE;
    for (const log of this.auditLogs) {
      if ((counts.get(log.changeSetId) ?? 0) > 1 && !colors.has(log.changeSetId)) {
        colors.set(log.changeSetId, palette[next % palette.length]);
        next++;
      }
    }

    this.changeSetColors = colors;
  }

  onSortChange(sort: Sort): void {
    if (sort.direction) {
      this.sortBy = sort.active;
      this.ascending = sort.direction === 'asc';
      this.loadAuditLogs();
    }
  }

  onPageChange(event: PageEvent): void {
    this.currentPage = event.pageIndex + 1;
    this.pageSize = event.pageSize;
    this.loadAuditLogs();
  }

  toggleSearch(): void {
    this.isSearchVisible = !this.isSearchVisible;
  }

  onSearchFilterChange(searchFilter: AuditLogSearchFilter): void {
    this.currentSearchFilter = searchFilter;
    this.currentPage = 1;
    this.loadAuditLogs();
  }
}
